/**
 * /moniteur/eleves — Élèves du moniteur.
 * 2 sources : (1) fiches secrétariat assignées via linked_teacher_id
 *              (2) élèves persos = contact_name distincts des perso_events.
 *
 * NOUVEAU (Option A) : bouton "+ Nouvel élève" → modale mini-fiche
 * (nom, email, téléphone, formule) qui crée une vraie fiche sec_students
 * auto-assignée à l'enseignant.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Users, ClipboardList, Calendar, Plus, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const FORMULES = [
  { key: 'permis_b', label: 'Permis B (voiture) · 20h' },
  { key: 'aac', label: 'Conduite accompagnée (AAC) · 20h' },
  { key: 'permis_b78', label: 'Permis B78 (BEA) · 13h' },
  { key: 'permis_a1', label: 'Permis A1 (moto 125cm³) · 20h' },
  { key: 'permis_a2', label: 'Permis A2 (moto) · 20h' },
  { key: 'passerelle_a', label: 'Passerelle A2→A · 7h' },
  { key: 'formation_125', label: 'Formation 125cm³ · 7h' },
  { key: 'code_seul', label: 'Code seul' },
];

export default function MoniteurElevesPage() {
  const [secEleves, setSecEleves] = useState([]);
  const [teacherLinked, setTeacherLinked] = useState(true);
  const [secLoadErr, setSecLoadErr] = useState(false);
  const [persoEleves, setPersoEleves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.boMoniteurListEleves();
      setSecEleves(r?.items || []);
      setTeacherLinked(r?.teacher_linked !== false);
      setSecLoadErr(false);
    } catch (_) {
      setSecLoadErr(true);
    }
    try {
      const p = await api.persoListMesEleves();
      setPersoEleves(p?.items || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'Mes élèves · Espace enseignant';
    loadData();
  }, [loadData]);

  return (
    <div className="max-w-4xl" data-testid="moniteur-eleves-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Enseignant · Élèves</div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Mes élèves</h1>
          <p className="text-sm text-white/60 mt-2">Ta liste d’élèves — issus du secrétariat OU créés depuis ton BO.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="btn-open-new-eleve"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-[13px] font-semibold shadow-md shadow-rose-900/30 transition"
        >
          <Plus size={15} /> Nouvel élève
        </button>
      </div>

      {loading && <div className="text-white/40 text-sm">Chargement…</div>}

      {!loading && !teacherLinked && (
        <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex items-start gap-3" data-testid="teacher-not-linked-warning">
          <AlertCircle className="text-amber-400 shrink-0 mt-0.5" size={18} />
          <div className="text-[13px] text-amber-100/90">
            <strong>Compte non lié à une fiche moniteur.</strong> Tes créations d&apos;élèves sont bloquées côté secrétariat.
            Demande à l&apos;administrateur de créer ta fiche enseignant avec ton email.
          </div>
        </div>
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-4">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-2"><ClipboardList size={15} /></div>
              <div className="text-3xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{secLoadErr ? '—' : secEleves.length}</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold mt-1">Assignés / créés</div>
            </div>
            <div className="rounded-2xl border border-rose-500/25 bg-rose-500/[0.04] p-4">
              <div className="w-9 h-9 rounded-lg bg-rose-500/20 text-rose-400 flex items-center justify-center mb-2"><Users size={15} /></div>
              <div className="text-3xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{persoEleves.length}</div>
              <div className="text-[10px] uppercase tracking-widest text-rose-400 font-semibold mt-1">Élèves persos</div>
            </div>
          </div>

          {secEleves.length > 0 && (
            <>
              <h2 className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-2">Fiches secrétariat</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="sec-eleves-list">
                {secEleves.map((e) => (
                  <div key={e.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 flex items-center justify-center flex-shrink-0 font-black">
                      {(e.name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold truncate">{e.name}</div>
                      <div className="text-white/50 text-xs mt-1 flex items-center gap-1.5 flex-wrap">
                        <span>{FORMULES.find(f => f.key === e.formula)?.label?.split('·')[0]?.trim() || e.formula}</span>
                        <span className="text-white/25">·</span>
                        <span>{e.hours_done || 0}h / {e.hours_package || 0}h</span>
                      </div>
                      {e.email && <div className="text-white/40 text-[11px] mt-0.5 truncate">{e.email}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <h2 className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-2">Mes élèves persos</h2>
          {persoEleves.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center" data-testid="perso-eleves-empty">
              <Users className="w-8 h-8 text-white/30 mx-auto mb-2" />
              <div className="text-white/60 text-sm">Aucun élève perso pour le moment.</div>
              <div className="text-white/40 text-xs mt-1">Ils apparaîtront ici dès que tu ajouteras un cours perso avec un nom d’élève dans <strong>Ma journée · Planning</strong>.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="perso-eleves-list">
              {persoEleves.map((e, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-rose-500/20 border border-rose-500/30 text-rose-400 flex items-center justify-center flex-shrink-0 font-black">
                    {(e.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold">{e.name}</div>
                    <div className="text-white/50 text-xs mt-1 flex items-center gap-1.5">
                      <Calendar size={11} /> {e.sessions_count} séance{e.sessions_count > 1 ? 's' : ''}
                      {e.last_date && ` · dernier ${new Date(e.last_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showModal && (
        <NewEleveModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); loadData(); }}
        />
      )}
    </div>
  );
}

// ─── Modale de création rapide ────────────────────────────────────────────
function NewEleveModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', formula: 'permis_b', auto_ecole_name: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = form.name.trim().length >= 2 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSaving(true);
    try {
      const created = await api.boMoniteurCreateEleve({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        formula: form.formula,
        auto_ecole_name: form.auto_ecole_name.trim(),
      });
      toast.success(`Élève créé : ${created.name}`, {
        description: 'Fiche visible chez le secrétariat',
        icon: <CheckCircle2 className="text-emerald-400" size={16} />,
      });
      onCreated();
    } catch (err) {
      const msg = err?.message || 'Erreur inconnue';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="new-eleve-modal-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111112] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="new-eleve-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-rose-400">Nouveau · Enseignant</div>
            <h2 className="text-xl font-black text-white mt-0.5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Créer un élève</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-white/10 bg-white/[0.03] text-white/60 hover:bg-white/10 hover:text-white transition inline-flex items-center justify-center"
            data-testid="btn-close-new-eleve-modal"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">
              Nom complet <span className="text-rose-400">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="ex. Julien Dupont"
              autoFocus
              required
              minLength={2}
              maxLength={100}
              className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-white/[0.03] text-white text-[14px] placeholder-white/25 focus:outline-none focus:border-rose-500/60 focus:bg-white/[0.05] transition"
              data-testid="input-eleve-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="julien@…"
                className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-white/[0.03] text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60 focus:bg-white/[0.05] transition"
                data-testid="input-eleve-email"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Téléphone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="06…"
                className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-white/[0.03] text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60 focus:bg-white/[0.05] transition"
                data-testid="input-eleve-phone"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">
              Formule <span className="text-rose-400">*</span>
            </label>
            <select
              value={form.formula}
              onChange={(e) => setForm({ ...form, formula: e.target.value })}
              className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-white/[0.03] text-white text-[13px] focus:outline-none focus:border-rose-500/60 focus:bg-white/[0.05] transition"
              data-testid="select-eleve-formula"
            >
              {FORMULES.map((f) => (
                <option key={f.key} value={f.key} className="bg-[#111112]">{f.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">
              Auto-école de l&apos;élève <span className="text-white/30 normal-case tracking-normal text-[10.5px]">(optionnel, peut différer de la tienne)</span>
            </label>
            <input
              type="text"
              value={form.auto_ecole_name}
              onChange={(e) => setForm({ ...form, auto_ecole_name: e.target.value })}
              placeholder="ex. Auto-École du Centre"
              maxLength={100}
              className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-white/[0.03] text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60 focus:bg-white/[0.05] transition"
              data-testid="input-eleve-auto-ecole"
            />
          </div>

          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/[0.08] p-3 flex items-start gap-2 text-[12px] text-red-200" data-testid="new-eleve-error">
              <AlertCircle className="shrink-0 mt-0.5" size={14} />
              <span>{error}</span>
            </div>
          )}

          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white/60 hover:text-white hover:bg-white/[0.06] transition"
              data-testid="btn-cancel-new-eleve"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-semibold shadow-md shadow-rose-900/30 transition"
              data-testid="btn-submit-new-eleve"
            >
              {saving ? (<><Loader2 size={14} className="animate-spin" /> Création…</>) : (<><Plus size={14} /> Créer l&apos;élève</>)}
            </button>
          </div>

          <p className="text-[11px] text-white/40 pt-1 border-t border-white/[0.05] mt-2 pt-3">
            La fiche apparaîtra dans le secrétariat et te sera automatiquement assignée.
          </p>
        </form>
      </div>
    </div>
  );
}
